import React, { useMemo, useState } from "react";

import { type ChartData, type ChartDataset } from "chart.js";
import moment from "moment";
import { Line } from "react-chartjs-2";

import { useUser } from "@components/user-context";
import { type Kpi } from "@features/mmm/components/explore-budgets/client-setting/types";
import { type FetchMmmSummary_me_mmm_summary_accuracyData_data } from "@north-beam/nb-client-side";
import { RecapPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { Checkbox } from "@shared/checkbox";

import { plugins } from "./plugins";
import {
  DATASET_ACTUAL_COLOR,
  DATASET_ACTUAL_LABEL,
  DATASET_ACTUAL_LEGEND,
  DATASET_PREDICTION_COLOR,
  DATASET_PREDICTION_LABEL,
  DATASET_PREDICTION_LEGEND,
  DATASET_PREDICTION_RANGE_LABEL,
  DATASET_PREDICTION_RANGE_LEGEND,
} from "./utils/chart-dataset-helpers";
import { getChartOptions } from "./weekly-accuracy-chart-options";

interface Props {
  accuracyData: FetchMmmSummary_me_mmm_summary_accuracyData_data[];
  selectedKpi: Kpi;
}

export const WeeklyAccuracyChart = ({ accuracyData, selectedKpi }: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.RECAP_PERFORMANCE);
  const { user } = useUser();

  const [datasetVisibility, setDatasetVisibility] = useState<
    ("actual" | "predicted" | "predicted-range")[]
  >(["actual", "predicted", "predicted-range"]);

  const data: ChartData<any, { x: Date; y: number }[]> = useMemo(() => {
    const labels = accuracyData.map((e) => {
      const periodStart = moment(e.date).format("MMM DD, YYYY");
      const periodEnd = moment(e.date).add(6, "days").format("MMM DD, YYYY");

      return `${periodStart} - ${periodEnd}`;
    });

    const datasets: ChartDataset<any, { x: Date; y: number }[]>[] = [
      {
        label: DATASET_ACTUAL_LABEL,
        data: accuracyData.map((e) => e.actualKpis[selectedKpi.kpi_name]),
        borderColor: DATASET_ACTUAL_COLOR,
        backgroundColor: DATASET_ACTUAL_COLOR,
        yAxisID: "y",
        hidden: !datasetVisibility.includes("actual"),
        pointRadius: 2.5,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
      },
      {
        label: DATASET_PREDICTION_LABEL,
        data: accuracyData.map(
          (e) => e.predictedKpis[selectedKpi.kpi_name].value,
        ),
        borderColor: DATASET_PREDICTION_COLOR,
        backgroundColor: DATASET_PREDICTION_COLOR,
        yAxisID: "y",
        hidden: !datasetVisibility.includes("predicted"),
        pointRadius: 2.5,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
      },
      {
        label: DATASET_PREDICTION_RANGE_LABEL,
        data: accuracyData.map(
          (e) => e.predictedKpis[selectedKpi.kpi_name].high,
        ),
        backgroundColor: "rgba(113,128,254, 0.2)",
        fill: "+1",
        yAxisID: "y",
        hidden: !datasetVisibility.includes("predicted-range"),
        pointRadius: 0,
        borderWidth: 0,
        tension: 0.5,
        animation: false,
      },
      {
        label: DATASET_PREDICTION_RANGE_LABEL,
        data: accuracyData.map(
          (e) => e.predictedKpis[selectedKpi.kpi_name].low,
        ),
        yAxisID: "y",
        hidden: !datasetVisibility.includes("predicted-range"),
        pointRadius: 0,
        borderWidth: 0,
        tension: 0.5,
        animation: false,
      },
    ];

    return { labels, datasets };
  }, [datasetVisibility, accuracyData, selectedKpi]);

  return (
    <div className="pb-4">
      <div className="flex items-center relative justify-center">
        <div className=" absolute left-0 font-bold text-sm">
          {selectedKpi.friendly_name}
        </div>
        <div className="flex relative text-xs space-x-5">
          <div className="flex items-center space-x-0.5">
            <label className="flex items-center gap-2">
              <Checkbox
                className="mr-2"
                value={datasetVisibility.includes("actual")}
                onChange={(value) => {
                  trackMMMEvent({
                    component:
                      RecapPerformanceTrackedComponent.WEEKLY_ACCURACY_CHART,
                    action: "onChange",
                    actionInfo: "Toggle 'Actual' dataset visiblity",
                    data: getMMMEventSelectionData(String(value)),
                  });

                  if (value) {
                    setDatasetVisibility([...datasetVisibility, "actual"]);
                  } else {
                    setDatasetVisibility(
                      datasetVisibility.filter((e) => e !== "actual"),
                    );
                  }
                }}
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: DATASET_ACTUAL_LEGEND,
                }}
              />
              <div>Actual</div>
            </label>
          </div>
          <div className="flex items-center space-x-0.5">
            <label className="flex items-center gap-2">
              <Checkbox
                className="mr-2"
                value={datasetVisibility.includes("predicted")}
                onChange={(value) => {
                  trackMMMEvent({
                    component:
                      RecapPerformanceTrackedComponent.WEEKLY_ACCURACY_CHART,
                    action: "onChange",
                    actionInfo: "Toggle 'Predicted' dataset visibility",
                    data: getMMMEventSelectionData(String(value)),
                  });

                  if (value) {
                    setDatasetVisibility([...datasetVisibility, "predicted"]);
                  } else {
                    setDatasetVisibility(
                      datasetVisibility.filter((e) => e !== "predicted"),
                    );
                  }
                }}
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: DATASET_PREDICTION_LEGEND,
                }}
              />
              <div>Predicted</div>
            </label>
          </div>
          <div className="flex items-center space-x-1.5">
            <label className="flex items-center gap-2">
              <Checkbox
                className="mr-1"
                value={datasetVisibility.includes("predicted-range")}
                onChange={(value) => {
                  trackMMMEvent({
                    component:
                      RecapPerformanceTrackedComponent.WEEKLY_ACCURACY_CHART,
                    action: "onChange",
                    actionInfo: "Toggle 'Predicted range' dataset visibility",
                    data: getMMMEventSelectionData(String(value)),
                  });

                  if (value) {
                    setDatasetVisibility([
                      ...datasetVisibility,
                      "predicted-range",
                    ]);
                  } else {
                    setDatasetVisibility(
                      datasetVisibility.filter((e) => e !== "predicted-range"),
                    );
                  }
                }}
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: DATASET_PREDICTION_RANGE_LEGEND,
                }}
              />
              <div>Predicted range</div>
            </label>
          </div>
        </div>
      </div>
      <div className="h-96">
        <Line
          options={getChartOptions(user, selectedKpi, data)}
          data={data}
          plugins={plugins}
        />
      </div>
    </div>
  );
};
